import { Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { useCallback, useRef, type ReactElement, type JSX } from 'react';

import { DataTableRow } from './DataTableRow.js';
import { useDataTable } from '../contexts/DataTableContext.js';
import { RowDetailsElement } from '../features/RowDetails/RowDetailsElement.js';
import { RowOrderingDragAndDropContext } from '../features/RowOrder/RowOrder.js';
import { type Row } from '../hooks/useTable/types.js';
import type { DataTableRowData } from '../public.api.js';
import * as styles from './DataTableBody.sty.js';
import {
  DATA_TABLE_DETAILS_ROW_IDENTIFIER,
  DATA_TABLE_DETAILS_ROW_PARITY,
  DATA_TABLE_ROW_EXPANDED_ID_SUFFIX,
} from '../constants.js';

export function DataTableBody<TData extends DataTableRowData>(
  props: Readonly<{
    rows: Row<TData>[];
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
    columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  }>,
): ReactElement | null {
  const { rows, rowVirtualizer, columnVirtualizer } = props;
  const table = useDataTable();

  // Keep a list of all rows that are currently measured by the virtualizer
  const queuedMeasurementRowIdRef = useRef(new Set());

  const rowMeasure = useCallback(
    (node: Element | null): void => {
      if (!node || !(node instanceof HTMLElement)) {
        return;
      }
      const rowId = node.getAttribute('data-rowid');
      if (rowId === null || queuedMeasurementRowIdRef.current.has(rowId)) {
        return;
      }
      queuedMeasurementRowIdRef.current.add(rowId);
      requestAnimationFrame(() => {
        // Ensure the virtualizer only measures element that are still connected to the DOM. Detached elements will be ignored.
        if (node.isConnected) {
          rowVirtualizer.measureElement(node);
        }
        // Remove the row from the queued measurement list, so it can be measured again in the future.
        queuedMeasurementRowIdRef.current.delete(rowId);
      });
    },
    [rowVirtualizer],
  );

  const columnMeasure = useCallback(
    (node: Element | null) => {
      if (!node || !(node instanceof HTMLElement)) {
        return;
      }
      columnVirtualizer.measureElement(node);
    },
    [columnVirtualizer],
  );

  const firstRenderedRowIndex = rows.at(0)?.index;
  const rowSeparation = table.getVisualVariantOptions().rowSeparation;
  const virtualColumns = columnVirtualizer.getVirtualItems();
  const draggingRowId = table.getState().draggingRowInfo?.id;
  const draggingRowIdExpanded = `${draggingRowId}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`;

  const rowElements: JSX.Element[] = rowVirtualizer
    .getVirtualItems()
    .map((virtualRow, index) => {
      const originalRow = rows[virtualRow.index];
      let parity: 'even' | 'odd' = virtualRow.index % 2 === 0 ? 'even' : 'odd';
      const isDraggedRow =
        draggingRowId === undefined
          ? false
          : originalRow.id === draggingRowId ||
            originalRow.id === draggingRowIdExpanded;

      // If the row parity is set in the row, use this one as it
      // could change via row details expansion.
      const detailsParity = originalRow[DATA_TABLE_DETAILS_ROW_PARITY];
      if (detailsParity !== undefined) {
        parity = detailsParity;
      }

      // Need to run this through the symbol accessor instead of the
      // feature, as the feature does not get the information from the
      // extended row model, as the row model runs later.
      if (originalRow[DATA_TABLE_DETAILS_ROW_IDENTIFIER] === true) {
        return (
          <RowDetailsElement
            key={originalRow.id}
            originalRow={originalRow}
            virtualRow={virtualRow}
            virtualItemIndex={index}
            rowSeparation={rowSeparation}
            rowParity={parity}
            isLastRowOnPage={virtualRow.index === rows.length - 1}
            rowMeasure={rowMeasure}
            isDraggedRow={isDraggedRow}
          >
            {table.options.rowDetailsTemplate?.({
              row: originalRow.original,
            })}
          </RowDetailsElement>
        );
      }

      return (
        <DataTableRow
          key={`row-${virtualRow.key}`}
          row={originalRow}
          virtualRow={virtualRow}
          virtualItemIndex={index}
          rowCount={rows.length}
          virtualColumns={virtualColumns}
          isDraggedRow={isDraggedRow}
          isInDragOverlay={false}
          rowParity={parity}
          rowMeasure={rowMeasure}
          columnMeasure={
            // For columns, only measure the first rendered row.
            // All other rows will have the same column widths.
            firstRenderedRowIndex === virtualRow.index
              ? columnMeasure
              : undefined
          }
          scrollToIndex={rowVirtualizer.scrollToIndex}
        />
      );
    });

  const bodyContent = table.options.enableRowOrderDragAndDrop ? (
    <RowOrderingDragAndDropContext<TData>
      rows={rows}
      virtualColumns={virtualColumns}
      rowSeparation={rowSeparation}
    >
      {rowElements}
    </RowOrderingDragAndDropContext>
  ) : (
    // If row order drag and drop is not enabled then don't use the RowOrderingDragAndDropContext
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{rowElements}</>
  );

  return (
    <div
      role="rowgroup"
      data-rowgroup="body"
      className={clsx(styles.body, {
        [styles.loading]: table.getState().loading,
      })}
    >
      {bodyContent}
    </div>
  );
}

DataTableBody.displayName = 'DataTableBody';
